import cn from 'classnames';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { Icon } from '@.ui';
import { DictionaryItem } from '@/Types/api/Dictionaries';

import { mapDictionaryItemToListItemModel, mapListItemModelToDictionaryItem } from '../../../lib';
import { ListItemModel } from '../../../model/ListItemModel';
import { StoreClusterMode, storeClusterStoreSelectors } from '../../../model/StoreClusterStore';
import { Listbox } from './components';
import S from './StoreClusterFormDualListbox.module.scss';

interface StoreClusterEditableListProps {
    title: ReactNode;
    placeholder: string;
}

export function StoreClusterFormDualListbox(props: StoreClusterEditableListProps) {
    const companies = storeClusterStoreSelectors.use.companies();
    const storeGroups = storeClusterStoreSelectors.use.storeGroups();
    const stores = storeClusterStoreSelectors.use.stores();

    const getCurrentBusyCollectionIdsByMode = storeClusterStoreSelectors.use.getCurrentBusyCollectionIdsByMode();

    const selectedCompanies = storeClusterStoreSelectors.use.selectedCompanies();
    const selectedStoreGroups = storeClusterStoreSelectors.use.selectedStoreGroups();
    const selectedStores = storeClusterStoreSelectors.use.selectedStores();

    const setSelectedDictionaryItems = storeClusterStoreSelectors.use.setSelectedDictionaryItems();
    const deselectDictionaryItems = storeClusterStoreSelectors.use.deselectDictionaryItems();

    const mode = storeClusterStoreSelectors.use.mode();

    const entitiesToSelect = useMemo<DictionaryItem[]>(() => {
        switch (mode) {
            case StoreClusterMode.COMPANIES:
                return companies;
            case StoreClusterMode.STORE_GROUPS:
                return storeGroups;
            case StoreClusterMode.STORES:
                return stores;
        }
    }, [companies, mode, storeGroups, stores]);

    const selectedEntitiesToSelect = useMemo<DictionaryItem[]>(() => {
        switch (mode) {
            case StoreClusterMode.COMPANIES:
                return selectedCompanies;
            case StoreClusterMode.STORE_GROUPS:
                return selectedStoreGroups;
            case StoreClusterMode.STORES:
                return selectedStores;
        }
    }, [mode, selectedCompanies, selectedStoreGroups, selectedStores]);

    const [allEntities, setAllEntities] = useState<ListItemModel[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<ListItemModel[]>([]);

    /**
     * When change mode, then change entities list for left and right list.
     */
    useEffect(() => {
        const currentBusyCollectionIdsByMode = getCurrentBusyCollectionIdsByMode();

        const selectedEntities = selectedEntitiesToSelect.map((element) => mapDictionaryItemToListItemModel(element, []));

        const allEntities = entitiesToSelect
            .map((element) => mapDictionaryItemToListItemModel(element, currentBusyCollectionIdsByMode))
            .filter((entity) => {
                return !selectedEntities.some((item) => item.entityId === entity.entityId);
            });

        setAllEntities(allEntities);
        setSelectedEntities(selectedEntities);
    }, [mode, entitiesToSelect, selectedEntitiesToSelect, getCurrentBusyCollectionIdsByMode]);

    const createSelectEntitiesFn = (type: 'all' | 'selected') => (item: ListItemModel) => {
        const entitiesToUpdate = type === 'all' ? allEntities : selectedEntities;
        const entitiesToUnselectedAll = type === 'all' ? selectedEntities : allEntities;
        const setterSelectedEntities = type === 'all' ? setAllEntities : setSelectedEntities;
        const setterUnselectEntitiesAll = type === 'all' ? setSelectedEntities : setAllEntities;

        const nextEntitiesStateWithSelected = entitiesToUpdate.map((entity) => {
            if (entity.entityId === item.entityId) {
                return item;
            }

            return entity;
        });

        const nextEntitiesStateAllUnselected = entitiesToUnselectedAll.map((item) => ({
            ...item,
            isSelected: false,
        }));

        setterSelectedEntities(nextEntitiesStateWithSelected);
        setterUnselectEntitiesAll(nextEntitiesStateAllUnselected);
    };

    const createUnselectEntitiesFn = (type: 'all' | 'selected') => (item: ListItemModel) => {
        const entitiesToUpdate = type === 'all' ? allEntities : selectedEntities;
        const setterSelectedEntities = type === 'all' ? setAllEntities : setSelectedEntities;

        const nextEntitiesState = entitiesToUpdate.map((entity) => {
            if (entity.entityId === item.entityId) {
                return item;
            }

            return entity;
        });

        setterSelectedEntities(nextEntitiesState);
    };

    const createChangeAllSelectedStateEntitiesFn = (type: 'all' | 'selected', isSelected: boolean) => () => {
        const setterSelectedEntities = type === 'all' ? setAllEntities : setSelectedEntities;

        setterSelectedEntities((entities) =>
            entities.map((entity) => ({
                ...entity,
                isSelected: entity.isDisabled ? entity.isSelected : isSelected,
            }))
        );
    };

    const moveToSelected = async () => {
        const entities = allEntities.filter((entity) => entity.isSelected && !entity.isDisabled);

        await setSelectedDictionaryItems(entities.map(mapListItemModelToDictionaryItem), mode);
    };

    const moveFromSelectedToUnselected = async () => {
        const entities = selectedEntities.filter((entity) => !entity.isSelected);

        await deselectDictionaryItems(entities.map(mapListItemModelToDictionaryItem), mode);
    };

    const isSomeOneEntityIsSelected = (entities: ListItemModel[]): boolean => {
        return entities.some((entity) => entity.isSelected);
    };

    const isAllowMoveToSelected = isSomeOneEntityIsSelected(allEntities);
    const isAllowMoveFromSelectedToUnselected = isSomeOneEntityIsSelected(selectedEntities);

    const dualListBoxTextsByMode = useMemo(() => {
        switch (mode) {
            case StoreClusterMode.COMPANIES:
                return {
                    title: 'Available companies',
                    rightListTitle: 'Selected companies',
                    placeholder: 'Search by name of companies',
                };
            case StoreClusterMode.STORE_GROUPS:
                return {
                    title: 'Available store groups',
                    rightListTitle: 'Selected store groups',
                    placeholder: 'Search by name of store groups',
                };
            case StoreClusterMode.STORES:
                return {
                    title: 'Available store groups',
                    rightListTitle: 'Selected stores',
                    placeholder: 'Search by name of store groups',
                };
        }
    }, [mode]);

    return (
        <div className={S.root}>
            <Listbox
                title={dualListBoxTextsByMode.title}
                placeholder={dualListBoxTextsByMode.placeholder}
                items={allEntities}
                selectItem={createSelectEntitiesFn('all')}
                unselectItem={createUnselectEntitiesFn('all')}
                selectAll={createChangeAllSelectedStateEntitiesFn('all', true)}
                unselectAll={createChangeAllSelectedStateEntitiesFn('all', false)}
            />

            <div className={S.controls}>
                <div className={cn(S.control, !isAllowMoveToSelected && S.control_disabled)} onClick={moveToSelected}>
                    <Icon name="arrow-right" size={24} />
                </div>
                <div className={cn(S.control, !isAllowMoveFromSelectedToUnselected && S.control_disabled)} onClick={moveFromSelectedToUnselected}>
                    <Icon className={S.arrowLeft} name="arrow-right" size={24} />
                </div>
            </div>

            <Listbox
                title={dualListBoxTextsByMode.rightListTitle}
                placeholder={dualListBoxTextsByMode.placeholder}
                items={selectedEntities}
                selectItem={createSelectEntitiesFn('selected')}
                unselectItem={createUnselectEntitiesFn('selected')}
                selectAll={createChangeAllSelectedStateEntitiesFn('selected', true)}
                unselectAll={createChangeAllSelectedStateEntitiesFn('selected', false)}
            />
        </div>
    );
}
